import { Button, Skeleton, Spinner } from '@design-system';

import { Calendar, Mail } from 'lucide-react';

import { useEffect, useState } from 'react';

import { analytics } from '@client/analytics';
import { useSendTwoPagerEmail } from '@/features/advisor-onboarding/questionnaire/hooks/use-send-two-pager-email';

import type { BgaPartnerInterest, UserIntent } from '@shared';

const LOADING_MS = 1500;

const EVALUATOR_BOOK_A_MEETING_URL =
  'https://calendly.com/d/dz4z-w9w-rqx/waterlily-onboarding-and-training';

interface QuestionnaireFinalScreenProps {
  advisorId: string;
  firstName: string | null;
  userIntent: UserIntent | null;
  partnerInterest: BgaPartnerInterest | null;
  onStartFreeTrial: () => void;
}

function getHeaderCopy(
  firstName: string | null,
  userIntent: UserIntent | null,
  partnerInterest: BgaPartnerInterest | null,
): { title: string; subtitle: string | null } {
  const nameSuffix = firstName ? `, ${firstName}` : '';
  if (userIntent === 'evaluating') {
    return {
      title: `Thanks${nameSuffix}.`,
      subtitle: "Let's set up the right evaluation for your team.",
    };
  }
  if (partnerInterest === 'connect-with-partners' || partnerInterest === 'explore-waterlily-bga') {
    return {
      title: `You're all set${nameSuffix}.`,
      subtitle: 'Our team is reaching out today about contracting — keep an eye on your inbox.',
    };
  }
  return {
    title: `You're all set${nameSuffix}.`,
    subtitle:
      'Welcome to Waterlily. Our team will review your responses and reach out today with tips and 1:1 support tailored to making you successful.',
  };
}

export function QuestionnaireFinalScreen({
  advisorId,
  firstName,
  userIntent,
  partnerInterest,
  onStartFreeTrial,
}: QuestionnaireFinalScreenProps) {
  const [isLoading, setIsLoading] = useState(true);
  const { sendTwoPager, isSendingTwoPager } = useSendTwoPagerEmail(advisorId);

  useEffect(() => {
    const timer = window.setTimeout(() => setIsLoading(false), LOADING_MS);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      analytics.track(
        'advisor_onboarding_questionnaire_final_screen_viewed',
        { userIntent, partnerInterest },
        'engagement',
      );
    }
  }, [isLoading, userIntent, partnerInterest]);

  const trimmedName = firstName?.trim() || null;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center px-4 sm:px-0">
        <div className="w-full max-w-lg space-y-6 text-center">
          <div className="flex flex-col items-center gap-3">
            <Spinner />
            <p className="text-foreground text-base">
              {trimmedName
                ? `Setting things up for you, ${trimmedName}…`
                : 'Setting things up for you…'}
            </p>
          </div>
          <div className="space-y-3">
            <Skeleton className="mx-auto h-6 w-3/4" />
            <Skeleton className="mx-auto h-4 w-2/3" />
            <Skeleton className="mx-auto h-4 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  const { title, subtitle } = getHeaderCopy(trimmedName, userIntent, partnerInterest);

  const isEvaluator = userIntent === 'evaluating';

  const handleStartFreeTrial = () => {
    analytics.track(
      'advisor_onboarding_questionnaire_final_screen_cta_clicked',
      { cta: 'start_free_trial', userIntent, partnerInterest },
      'engagement',
    );
    onStartFreeTrial();
  };

  const handleBookEvaluatorMeeting = () => {
    analytics.track(
      'advisor_onboarding_questionnaire_final_screen_cta_clicked',
      { cta: 'book_evaluation_call', userIntent, partnerInterest },
      'engagement',
    );
    window.open(EVALUATOR_BOOK_A_MEETING_URL, '_blank', 'noopener,noreferrer');
  };

  const handleRequestTwoPager = () => {
    analytics.track(
      'advisor_onboarding_questionnaire_final_screen_cta_clicked',
      { cta: 'email_two_pager', userIntent, partnerInterest },
      'engagement',
    );
    sendTwoPager();
  };

  return (
    <div className="flex flex-col items-center px-4 sm:px-0">
      <div className="bg-background border-border w-full max-w-lg space-y-6 rounded-2xl border p-8 text-center">
        <div className="space-y-3">
          <h2 className="text-2xl font-semibold text-gray-950 sm:text-3xl">{title}</h2>
          {subtitle && <p className="text-foreground text-base sm:text-lg">{subtitle}</p>}
        </div>

        <div className="flex flex-col gap-3">
          {isEvaluator ? (
            <>
              <Button
                size="lg"
                onClick={handleBookEvaluatorMeeting}
                className="rounded-full"
              >
                <Calendar />
                Book a 30-min evaluation call
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleRequestTwoPager}
                disabled={isSendingTwoPager}
                className="rounded-full"
              >
                <Mail />
                Email me a two-pager I can share with my team
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={handleStartFreeTrial}
                className="rounded-full"
              >
                Continue setup
              </Button>
            </>
          ) : (
            <Button
              size="lg"
              onClick={handleStartFreeTrial}
              className="rounded-full"
            >
              Start my free trial
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
